import React from 'react';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import { Typography } from 'antd';
import { AssetChildSortableTable } from 'entities/Asset/components/DnD/AssetChildSortableTable';
import { IAssetCategory, IAssetMenuItem } from 'entities/Asset/Asset.models';
import { ECommonEntityStatus } from 'entities/Bitecoin/Bitecoin.models';

interface IComponentProps {
  value?: IAssetCategory[];
  onChange?: (collection?: IAssetCategory[]) => void;
  filter?: string;
  onlyAddedToMenu?: boolean;
  onlyAddedToBuilder?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

type AllProps = IComponentProps;

// react-sortable-hoc's SortableElement reserves its own `disabled` prop (controls only that
// item's drag-ability) and strips it before forwarding props to the wrapped component — so the
// "lock this content" flag has to travel under a different name (`readOnly`) past that boundary.
const CategoryTable = SortableElement(
  ({
    item,
    onItemChange,
    readOnly,
  }: {
    item: IAssetCategory;
    onItemChange: (categoryItem: IAssetCategory) => void;
    readOnly?: boolean;
  }) => {
    return (
      <li key={item.id}>
        <AssetChildSortableTable item={item} onItemChange={onItemChange} disabled={readOnly} />
      </li>
    );
  }
);

const CategoryList = SortableContainer(({ items, onItemChange, readOnly }: any) => {
  return (
    <ul className="p-0">
      {items?.map((item: any, index: number) => (
        <CategoryTable key={item.id} index={index} item={item} onItemChange={onItemChange} readOnly={readOnly} />
      ))}
    </ul>
  );
});

export class AssetSortableParent extends React.PureComponent<AllProps> {
  getFilteredCategories() {
    const { filter, onlyAddedToMenu, onlyAddedToBuilder } = this.props;
    const { visible } = this.getVisibleAndHidden();
    
    if (!visible) {
      return [];
    }

    const text = filter?.toLowerCase();

    return visible
      .map((category) => {
        const categoryMatch = text ? category.name.toLowerCase().includes(text) : true;

        const filteredItems = category.menuItemsToAsset.filter((item) => {
          const textMatch = text ? item.name.toLowerCase().includes(text) : true;

          const menuMatch = onlyAddedToMenu ? item.isInMenu : true;
          const builderMatch = onlyAddedToBuilder ? category.isMealBuilder && item.isInMealBuilder : true;

          return textMatch && menuMatch && builderMatch;
        });

        if (categoryMatch && !(onlyAddedToMenu || onlyAddedToBuilder)) {
          return category;
        }

        if (filteredItems.length) {
          return {
            ...category,
            menuItemsToAsset: filteredItems,
          };
        }

        return null;
      })
      .filter(Boolean) as IAssetCategory[];
  }

  getVisibleAndHidden() {
    const { value } = this.props;

    if (!value) {
      return { visible: [], hiddenMap: new Map() };
    }

    const visible: IAssetCategory[] = [];
    const hiddenMap = new Map<string, IAssetCategory>();

    value.forEach((category) => {
      if (category.status !== ECommonEntityStatus.Active) {
        hiddenMap.set(category.id, category);
      } else {
        visible.push({
          ...category,
          menuItemsToAsset: category.menuItemsToAsset.filter((item) => item.status === ECommonEntityStatus.Active),
        });
      }
    });

    return { visible, hiddenMap };
  }

  // ponytail: перестановка отфильтрованного списка мапится на полный — видимые
  // категории меняются местами в своих слотах, скрытые остаются на местах
  onSortEnd = ({ oldIndex, newIndex }: any) => {
    const { onChange, value } = this.props;

    if (oldIndex === newIndex || !value) {
      return;
    }

    const orderedIds = arrayMove(
      this.getFilteredCategories().map((c) => c.id),
      oldIndex,
      newIndex
    );
    const inView = new Set(orderedIds);
    const byId = new Map(value.map((c) => [c.id, c] as [string, IAssetCategory]));
    const idQueue = [...orderedIds];

    const newData = value
      .map((category) => {
        if (!inView.has(category.id)) {
          return category;
        }

        const nextId = idQueue.shift();

        return (nextId && byId.get(nextId)) || category;
      })
      .map((category, index) => ({ ...category, orderNumber: index }));

    onChange && onChange(newData);
  };

  onItemChange = (categoryItem: IAssetCategory) => {
    const { onChange, value } = this.props;
    const newDataState: IAssetCategory[] | undefined = value?.map((originalItem) => {
      if (originalItem.id !== categoryItem.id) {
        return originalItem;
      }

      const changedById = new Map(categoryItem.menuItemsToAsset.map((m) => [m.id, m] as [string, IAssetMenuItem]));
      const idQueue = categoryItem.menuItemsToAsset.map((m) => m.id);

      const merged = originalItem.menuItemsToAsset
        .map((menuItem) => {
          if (!changedById.has(menuItem.id)) {
            return menuItem;
          }

          const nextId = idQueue.shift();

          return (nextId && changedById.get(nextId)) || menuItem;
        })
        .map((menuItem, index) => ({ ...menuItem, orderNumber: index }));

      return {
        ...categoryItem,
        menuItemsToAsset: merged,
      };
    });

    onChange && onChange(newDataState);
  };

  render() {
    const { loading, disabled } = this.props;
    const filtered = this.getFilteredCategories();

    if (!loading && filtered.length === 0) {
      return (
        <div>
          <Typography.Text type="secondary">Nothing found. Try changing search or filters.</Typography.Text>
        </div>
      );
    }

    return (
      <CategoryList
        items={filtered}
        distance={4}
        useDragHandle
        lockAxis="y"
        helperClass="asset-category-dragging"
        onSortEnd={this.onSortEnd}
        onItemChange={this.onItemChange}
        readOnly={disabled}
        shouldCancelStart={() => !!disabled}
      />
    );
  }
}
